import { IpFilterFormItem } from '../IpListField';

/** フォーム全体の値の型 */
type FormValues = {
    enableMfa: boolean;
    requireMfaPermission: string[];
    mfaSkipSameDevice: boolean;
    mfaSkipDays: number;
    mfaSkipHours: number;
    mfaAllowIpsEnabled: boolean;
    mfaAllowedIps: IpFilterFormItem[];
    requireMfaControl: boolean;
    controlIpFiltersEnabled: boolean;
    controlIpFilters: IpFilterFormItem[];
    requireMfaAdmin: boolean;
    adminIpFiltersEnabled: boolean;
    adminIpFilters: IpFilterFormItem[];
};

/** API に送信する IP フィルターの型 */
type SubmittableIpFilter = {
    setting: string;
    comment: string;
};

/** 多要素認証を設定可能にするユーザー権限のオブジェクト型 */
type RequireMfaPermission = {
    administrator: boolean;
    school_administrator: boolean;
    teacher: boolean;
    student: boolean;
};

/** API に送信するセキュリティ設定の型 */
export type SubmittableSecuritySettings = {
    security_setting: {
        enable_mfa: boolean;
        expire_mfa_hour: number;
        require_mfa_control: boolean;
        require_mfa_admin: boolean;
        require_mfa_permission: RequireMfaPermission;
        /** 同端末再認証間隔設定の有効フラグ */
        reauth_grace_period_enabled: boolean;
        /** 同端末再認証要求時間 (日数 × 24 + 時間) */
        reauth_grace_period: number;
    };
    mfa_allowed_ips: SubmittableIpFilter[];
    /** 多要素認証の許可 IP 制限を有効にするか否か (false の場合もリストは保持される) */
    mfa_allowed_ips_enabled: boolean;
    control_ip_filters: SubmittableIpFilter[];
    /** 全体管理者画面の IP 制限を有効にするか否か (false の場合もリストは保持される) */
    control_ip_filters_enabled: boolean;
    admin_ip_filters: SubmittableIpFilter[];
    /** 学校管理画面の IP 制限を有効にするか否か (false の場合もリストは保持される) */
    admin_ip_filters_enabled: boolean;
};

/** API レスポンスの IP フィルターアイテムの型 */
type ResponseIpFilter = {
    setting: string;
    comment: string;
    is_active: boolean;
};

/** API レスポンスのセキュリティ設定の型 */
type SecuritySettingsResponse = Http.Connection.Response.Security.GetSettings['result'];

/**
 * IP フィルターリストを API 送信可能な形式に変換する
 * 空の設定値・コメントを持つ項目を除外する
 */
const convertIpFilters = (filters: IpFilterFormItem[]): SubmittableIpFilter[] =>
    filters
        .filter(
            (item) =>
                (item.setting !== '' && item.setting !== undefined) ||
                (item.comment !== '' && item.comment !== undefined)
        )
        .map((item) => ({
            comment: item.comment ?? '',
            setting: item.setting ?? '',
        }));

/**
 * ネスト配列 (ResponseIpFilter[][]) を flatten する
 */
const flatten = (filters: ResponseIpFilter[][]): ResponseIpFilter[] => filters.flat();

/**
 * ネスト配列を flatten し、is_active === true のアイテムのみ抽出する
 * IP アドレス制限が有効か否か (制限する/制限しない) の判定に使用する
 */
const flatFilter = (filters: ResponseIpFilter[][]): ResponseIpFilter[] =>
    flatten(filters).filter((item) => item.is_active);

/**
 * IP フィルターリストをフォーム用の形式に変換する
 * レスポンスはネスト配列 (ResponseIpFilter[][]) のため flatten してから変換する
 * 「制限しない」(is_active === false) で保存されたアイテムも保持・表示するため、
 * is_active では絞り込まず全アイテムを変換する
 * created_at・is_active フィールドを除去し、フォームで扱える型に変換する
 * IP が未登録の場合は空配列を返す (UI 側では「＋」ボタンのみ表示する)
 */
const convertResponseIpFilters = (filters: ResponseIpFilter[][]): IpFilterFormItem[] =>
    flatten(filters).map((item) => ({
        comment: item.comment,
        setting: item.setting,
    }));

/**
 * フォームの値を API 送信可能な形式に変換する
 * camelCase のフォームフィールド名を snake_case の API パラメーター名にマッピングし、
 * 日数・時間を合算して expire_mfa_hour に変換する
 */
export const convertDataToRequestFormat = (values: FormValues): SubmittableSecuritySettings => ({
    admin_ip_filters: values?.adminIpFilters ? convertIpFilters(values.adminIpFilters): [],
    admin_ip_filters_enabled: values.adminIpFiltersEnabled ?? false,
    control_ip_filters: values?.controlIpFilters ? convertIpFilters(values.controlIpFilters): [],
    control_ip_filters_enabled: values.controlIpFiltersEnabled ?? false,
    mfa_allowed_ips: values?.mfaAllowedIps ? convertIpFilters(values.mfaAllowedIps): [],
    mfa_allowed_ips_enabled: values.mfaAllowIpsEnabled ?? false,
    security_setting: {
        enable_mfa: values.enableMfa,
        expire_mfa_hour: values.mfaSkipDays * 24 + values.mfaSkipHours,
        require_mfa_admin: values.requireMfaAdmin,
        require_mfa_control: values.requireMfaControl,
        require_mfa_permission: {
            administrator: values.requireMfaPermission.includes('administrator'),
            school_administrator: values.requireMfaPermission.includes('school_administrator'),
            teacher: values.requireMfaPermission.includes('teacher'),
            student: values.requireMfaPermission.includes('student'),
        },
        // 同端末再認証間隔設定の有効フラグ・要求時間 (UI の同一端末再認証スキップ設定に対応)
        reauth_grace_period_enabled: values.mfaSkipSameDevice,
        reauth_grace_period: values.mfaSkipDays * 24 + values.mfaSkipHours,
    },
});

/**
 * API レスポンスをフォームの値に変換する
 * snake_case の API レスポンスフィールドを camelCase のフォームフィールド名にマッピングする
 * IP アドレス制限の有効フラグは対応する配列の長さで判定する
 */
export const convertDataFromResponseFormat = (response: SecuritySettingsResponse): FormValues => ({
    enableMfa: response.security_setting?.enable_mfa ?? false,
    requireMfaPermission: Object.entries(response.security_setting?.require_mfa_permission ?? {})
        .filter(([, value]) => value === true)
        .map(([key]) => key),
    mfaSkipSameDevice: response.security_setting?.reauth_grace_period_enabled ?? false,
    // 未設定・0 時は既定値 3日0時間 (72h) を表示する。
    // (再要求スキップが有効なら backend の min=1 バリデーションで 0 は保存されないため、0 は実質「未設定」)
    mfaSkipDays: Math.floor((response.security_setting?.expire_mfa_hour || 72) / 24),
    mfaSkipHours: (response.security_setting?.expire_mfa_hour || 72) % 24,
    mfaAllowIpsEnabled: flatFilter(response.mfa_allowed_ips).length > 0,
    mfaAllowedIps: convertResponseIpFilters(response.mfa_allowed_ips),
    requireMfaControl: response.security_setting?.require_mfa_control ?? false,
    controlIpFiltersEnabled: flatFilter(response.control_ip_filters).length > 0,
    controlIpFilters: convertResponseIpFilters(response.control_ip_filters),
    requireMfaAdmin: response.security_setting?.require_mfa_admin ?? false,
    adminIpFiltersEnabled: flatFilter(response.admin_ip_filters).length > 0,
    adminIpFilters: convertResponseIpFilters(response.admin_ip_filters),
});

/**
 * MFA 有効化状態を判定する
 * enableMfa が true かつ requireMfaPermission に 'administrator' が含まれている場合のみ true を返す
 *
 * @param enableMfa - MFA 有効化フラグ
 * @param requireMfaPermission - MFA 必須権限リスト
 * @returns MFA 有効化状態
 */
export const getIsEnableMFAFromSetting = (
    enableMfa: boolean,
    requireMfaPermission: string[],
): boolean => {
    if (!enableMfa) return false;
    return requireMfaPermission.includes('administrator');
};